/* GTO Wizard ranges, read into ours.
   The solver writes a range combo by combo — "AcKc: 1,AcKd: 0.5901" — where this app writes
   hand classes with one weight each. Before a board there is nothing to tell one suit from
   another, so every combo of a class carries the same frequency and the 1326 numbers fold
   back into 169 without losing anything. This module is that fold, and nothing else: what
   comes out is notation, which the rest of the app already knows what to do with. */

import { RI, combosOf, r3 } from "./hands";
import { expandToken, notationFrom } from "./notation";

/** "AcKd": a rank and a suit, twice. Case is not part of the format. */
const COMBO = /^([akqjt2-9])([shdc])([akqjt2-9])([shdc])$/i;
/** "AKs", "AA": what this app writes, which the solver also accepts. */
const CLASS = /^([akqjt2-9])([akqjt2-9])([so])?$/i;
/** A frequency: "1", "0.5901", "4.6e-4". */
const FREQ = /^\d*\.?\d+(e-?\d+)?$/i;

/** What one entry of the list names: the class it belongs to, and how many combos it is. */
interface Named {
  hand: string;
  combos: number;
  /** The same thing written twice over is the same id: a combo either way round, a class. */
  id: string;
}

function nameOf(tok: string): Named {
  const c = COMBO.exec(tok);
  if (c) {
    const [, ra = "", sa = "", rb = "", sb = ""] = c;
    const [a, b] = [ra.toUpperCase(), rb.toUpperCase()];
    const [x, y] = [sa.toLowerCase(), sb.toLowerCase()];
    if (a === b && x === y) throw new Error(`"${tok}" is the same card twice`);
    const hand =
      a === b ? a + b : ((RI[a] ?? 0) < (RI[b] ?? 0) ? a + b : b + a) + (x === y ? "s" : "o");
    return { hand, combos: 1, id: [a + x, b + y].sort().join("") };
  }
  const k = CLASS.exec(tok);
  if (k) {
    const [, ra = "", rb = "", suf = ""] = k;
    // The notation's own reader, so a class is judged by the same rules wherever it is typed.
    const [hand = ""] = expandToken(ra.toUpperCase() + rb.toUpperCase() + suf.toLowerCase());
    return { hand, combos: combosOf(hand), id: hand };
  }
  throw new Error(`"${tok}" is not a hand`);
}

/** A range read out of GTO Wizard. */
export interface GtoRange {
  /** Hand class -> weight 0..1, rounded to the tenth of a percent the notation stores. */
  weights: Record<string, number>;
  /** The same thing as notation, ready for a field. */
  note: string;
  /** Combos read, and hand classes written. */
  combos: number;
  hands: number;
  /** Hands divided by an arriving weight below 1: 0 when nothing was read against one. */
  scaled: number;
  warn: string[];
}

/**
 * Is this text GTO Wizard's rather than ours? It names a combo by its suits, or gives a
 * weight after a colon; this app's notation does neither, since its tokens are two ranks and
 * an `s` or an `o` and its weights are `[50]…[/50]` tags.
 */
export function looksLikeGtoWizard(src: string | null | undefined): boolean {
  const s = (src ?? "").trim();
  if (!s || s.includes("[")) return false;
  return /[akqjt2-9][shdc][akqjt2-9][shdc]/i.test(s) || /[akqjt2-9]{2}[so]?\s*:\s*\d/i.test(s);
}

/**
 * A GTO Wizard range as weights and notation. Throws a readable error naming the entry that
 * stopped it: the text is machine-written, so one bad entry means the paste is not what it
 * was taken for, and half of it is worse than none.
 *
 * `arriving` is how often hero holds each hand in the spot being written — `presenceOf` on
 * the chart. The two formats measure from different places: the solver's weights are shares
 * of the whole deal, the same number in every chart down a line, while this app's are read
 * inside the range hero got here with. So a hand that opens a quarter of the time and then
 * calls every 3-bet reads 24.4% over there and 100% here, and the division is what turns one
 * into the other. Without a map nothing is divided, which is right for a chart hero has not
 * acted in yet.
 */
export function readGtoWizard(
  src: string | null | undefined,
  arriving?: ReadonlyMap<string, number> | null,
): GtoRange {
  const sum: Record<string, number> = {};
  const count: Record<string, number> = {};
  const seen = new Set<string>();
  const twice: string[] = [];
  let combos = 0;

  // Spaces around the colon first, so that "AA: 0.5" is one entry and not two.
  for (const entry of (src ?? "").replace(/\s*:\s*/g, ":").split(/[,;\s]+/)) {
    if (!entry) continue;
    const [tok = "", w, extra] = entry.split(":");
    if (extra !== undefined) throw new Error(`"${entry}" carries more than one weight`);
    if (w !== undefined && !FREQ.test(w)) throw new Error(`"${entry}" does not end in a weight`);
    const weight = w === undefined ? 1 : parseFloat(w);
    if (weight < 0 || weight > 1) throw new Error(`weights run from 0 to 1: "${entry}"`);
    const { hand, combos: n, id } = nameOf(tok);
    // A combo listed twice keeps its first weight: averaging the repeat in would weigh it
    // double, and the solver does not repeat itself on purpose.
    if (seen.has(id)) {
      twice.push(id);
      continue;
    }
    seen.add(id);
    combos += n;
    sum[hand] = (sum[hand] ?? 0) + weight * n;
    count[hand] = (count[hand] ?? 0) + n;
  }
  if (!combos) throw new Error("no hands in this range");

  const weights: Record<string, number> = {};
  const tiny: string[] = [];
  const part: string[] = [];
  const over: string[] = [];
  let scaled = 0;
  const pc = (w: number): string => (w * 100).toFixed(1).replace(/\.0$/, "") + "%";
  for (const [hand, n] of Object.entries(count)) {
    const full = combosOf(hand);
    if (n !== full) part.push(`${hand} ${n}/${full}`);
    // The mean over the combos given.
    const abs = (sum[hand] ?? 0) / n;
    // The solver counts from the whole deal and this app counts from the spot, so a weight
    // is divided by how often hero is here holding the hand at all. A hand hero does not
    // hold here is left as it came: it joins the chart at full presence, and the share of
    // deals it plays comes out the same either way.
    const p = arriving?.get(hand) ?? 1;
    if (p < 1) scaled++;
    // Solves disagree: a hand can act here more often than the chart above says it arrives.
    // All of it is the most that can be said, and it is worth saying out loud.
    if (abs > p + 0.0005) over.push(`${hand} ${pc(abs)} of ${pc(p)}`);
    // Rounded here and not by the writer, so that a weight too small to write does not come
    // out as "[0]88[/0]".
    const w = r3(p > 0 ? Math.min(1, abs / p) : abs);
    if (w > 0) weights[hand] = w;
    else tiny.push(hand);
  }

  const some = (what: string, list: string[]): string =>
    `${what}: ${list.slice(0, 6).join(", ")}${list.length > 6 ? `, +${list.length - 6} more` : ""}`;
  const warn: string[] = [];
  if (tiny.length) warn.push(some("under 0.05% and dropped", tiny));
  if (twice.length) warn.push(some("listed twice, the first kept", twice));
  if (part.length) warn.push(some("averaged over the combos given", part));
  if (over.length) warn.push(some("played more often than they arrive, taken as all of it", over));

  return {
    weights,
    note: notationFrom((h) => weights[h] ?? 0),
    combos,
    hands: Object.keys(weights).length,
    scaled,
    warn,
  };
}
